import { RSAA } from 'redux-api-middleware';
import get from 'lodash/get';
import values from 'lodash/values';
import { pages } from '../../states/page/actions';
import endpoints from '../../utils/endpoints';
import {
	getBeneficiaryId,
	getCustomerId,
	getCustomerType,
	getAccountType,
	getAccountIdentification,
	getAccountName,
	getRef,
	getCloseMatchDetails,
	wasLastOutcomeCloseMatch,
} from './selectors';
import { copReasonCodes, COP_MAX_TRY_COUNT } from '../../config';
import { setAccountNameServicesError } from '../../states/payees/actions';
import { submitDeletePayeeRequest, SUBMIT_DELETE_PAYEE_SUCCESS } from '../../managePayee/states/action';
import { submitAddPayeeRequest } from '../../payees/states/addPayee/actions';

export const SET_COP_DETAILS = 'SET_COP_DETAILS';
export const SUBMIT_COP_CHECK_REQUEST = 'SUBMIT_COP_CHECK_REQUEST';
export const SUBMIT_COP_CHECK_SUCCESS = 'SUBMIT_COP_CHECK_SUCCESS';
export const SUBMIT_COP_CHECK_FAILURE = 'SUBMIT_COP_CHECK_FAILURE';
export const GO_TO_COP_PAGE = 'GO_TO_COP_PAGE';
export const SET_COP_BENEFICIARY = 'SET_COP_BENEFICIARY';

/**
 * @todo Conform to FSA pattern (page -> payload)
 */
export function goToCOPPage(page) {
	return {
		type: GO_TO_COP_PAGE,
		page: page,
		meta: {
			allowedDuringApiCall: true,
		},
	};
}

export const setCopDetails = (copDetails) => ({
	type: SET_COP_DETAILS,
	copDetails,
});

export const setCOPBeneficiaryId = (id) => ({
	type: SET_COP_BENEFICIARY,
	payload: id,
});

const getCopCheckRequestBody = (state) => {
	const customer_id = getCustomerId(state);
	const customer_segment = getCustomerType(state);
	const beneficiary_uuid = getBeneficiaryId(state);
	const accountType = getAccountType(state);
	const identification = getAccountIdentification(state);
	const name = getAccountName(state);
	const secondaryIdentification = getRef(state);
	const lastOutcomeWasCloseMatch = wasLastOutcomeCloseMatch(state);

	return JSON.stringify({
		customer: {
			customer_id,
			customer_segment,
		},
		...(beneficiary_uuid && !lastOutcomeWasCloseMatch && { beneficiary: { beneficiary_uuid } }),
		account_name_verification: {
			scheme_name: 'SortCodeAccountNumber',
			account_type: accountType,
			identification: identification,
			name: name,
			secondary_identification: secondaryIdentification,
		},
	});
};

export const submitCopCheck = (state) => {
	const endpoint = endpoints.accountNameVerification;
	const body = getCopCheckRequestBody(state);

	return {
		[RSAA]: {
			endpoint: endpoint.uri,
			method: endpoint.method,
			types: [SUBMIT_COP_CHECK_REQUEST, SUBMIT_COP_CHECK_SUCCESS, SUBMIT_COP_CHECK_FAILURE],
			headers: {
				'x-bpi-version': endpoint.x_bpi_version,
			},
			body,
		},
	};
};

const isCloseMatchCode = (code) => values(copReasonCodes.closeMatch).includes(code);
const isErrorCode = (code) => [copReasonCodes.noMatch.ERR1].includes(code);

function getNoMatchOrBlocked(tryCount) {
	return tryCount >= COP_MAX_TRY_COUNT ? pages.COP.Blocked : pages.COP.NoMatch;
}

export const checkBlockedStatus = (tryCount, nextPage) => {
	const modifiedNextPage = nextPage === pages.COP.NoMatch ? getNoMatchOrBlocked(tryCount) : nextPage;
	return goToCOPPage(modifiedNextPage);
};

export const getPageFromResponse = (response) => {
	const result = get(response, 'payload.account_name_verification.verification_report', undefined);
	if (!result) {
		return pages.COP.ServiceDown;
	}
	if (result.matched) {
		return pages.COP.MatchMade;
	}
	if (isCloseMatchCode(result.reason_code)) {
		return pages.COP.CloseMatch;
	}
	if (isErrorCode(result.reason_code)) {
		return pages.COP.ServiceDown;
	}
	return pages.COP.NoMatch;
};

export const checkForMatch =
	({ accountNumber, copCheckTriggered, sortCode, tryCount }) =>
	(dispatch, getState) => {
		const state = getState();
		return dispatch(submitCopCheck(state)).then((response) => {
			if (response.error) {
				dispatch(setAccountNameServicesError());
			}

			const copReasonCode = get(response, 'payload.account_name_verification.verification_report.reason_code');

			const copAttemptDetails = {
				accountNumber,
				copReasonCode,
				sortCode,
			};

			const countsAsARetry = copReasonCodes.countAsRetryAttempt.hasOwnProperty(copReasonCode);
			const countsAsAFullMatch = copReasonCode === 'N/A';

			if (countsAsARetry) {
				copCheckTriggered(copAttemptDetails);
			} else if (countsAsAFullMatch) {
				copCheckTriggered(copAttemptDetails, true);
			}

			const nextPage = getPageFromResponse(response);
			dispatch(checkBlockedStatus(tryCount, nextPage));
		});
	};

export const updateDetails = () => (dispatch, getState) => {
	const copDetails = getCloseMatchDetails(getState());
	dispatch(setCopDetails(copDetails));
	const { accountNumber, sortCode } = copDetails;
	return dispatch(
		checkForMatch({
			accountNumber,
			copCheckTriggered: () => {},
			sortCode,
			tryCount: '1',
		})
	);
};

export const updateBeneficiary =
	({ id, sourceAccountId, payeeDetails, copResult }) =>
	(dispatch) =>
		dispatch(submitDeletePayeeRequest(sourceAccountId)).then((response) => {
			if (response.type === SUBMIT_DELETE_PAYEE_SUCCESS) {
				const account = { id: sourceAccountId };
				return dispatch(submitAddPayeeRequest({ id, payeeDetails, account, copDetails: copResult }));
			}
		});
